import { Trans, useLingui } from '@lingui/react/macro'
import {
  Badge,
  Box,
  Button,
  Card,
  Center,
  Divider,
  Group,
  Loader,
  Select,
  Stack,
  Text,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { AppPage } from '@markku/components'
import type {
  AttachmentResponse,
  MaintenanceLogWithRelations,
  VisitWithRelations,
} from '@markku/types'
import {
  assertNotNil,
  getCategoryColor,
  getCategoryLabel,
  isTruthy,
  sortBy,
} from '@markku/utils'
import { IconPhoto, IconPlus, IconUser } from '@tabler/icons-react'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import React, { useMemo, useState } from 'react'
import { maintenanceLogHooks, visitHooks } from '../../../lib/trpcHooks'
import { TaskDetailsModal } from '../../../modules/maintenance/components/TaskDetailsModal'

// Unified maintenance item interface
interface UnifiedMaintenanceItem {
  id: string
  completedDate: Date
  taskName: string
  description: string | null
  category: string | null
  notes: string | null
  isMarkku: boolean
  visitId: number | null
  attachments: AttachmentResponse[]
}

function MaintenanceLogPage() {
  const { t } = useLingui()
  const navigate = useNavigate()
  const [selectedYear, setSelectedYear] = useState<string | null>(
    new Date().getFullYear().toString(),
  )
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [addModalOpened, { open: openAddModal, close: closeAddModal }] =
    useDisclosure(false)

  // Get propertyId from URL search params (set by previous page)
  const search = useSearch({ from: '/_app/maintenance-log/' })
  const propertyId = assertNotNil(
    (search as any).propertyId,
    'propertyId must be provided in URL search params',
  )

  // Fetch maintenance logs from user-recorded tasks
  const { data: logsData, isLoading: logsLoading } =
    maintenanceLogHooks.useList({
      pageSize: 100,
      orderBy: 'completedDate',
      orderDirection: 'desc',
      filters: { propertyId: { eq: propertyId } },
    })

  // Fetch completed visits from Markku employees
  const { data: visitsData, isLoading: visitsLoading } = visitHooks.useList({
    pageSize: 100,
    orderBy: 'startTime',
    orderDirection: 'desc',
    filters: { propertyId: { eq: propertyId } },
  })

  const isLoading = logsLoading || visitsLoading

  // Transform and merge data from both sources
  const unifiedItems = useMemo<UnifiedMaintenanceItem[]>(() => {
    // Add items from maintenance logs (user-recorded)
    const logs = (logsData?.data || []).map(
      (log: MaintenanceLogWithRelations) => {
        if (log.completedDate) {
          return {
            id: `log-${log.id}`,
            completedDate: new Date(log.completedDate),
            taskName: log.taskNameSnapshot,
            description: log.descriptionSnapshot,
            category: log.maintenanceTask?.category || null,
            notes: log.notes,
            isMarkku: false,
            visitId: null,
            attachments: log.attachments || [],
          } satisfies UnifiedMaintenanceItem
        }
      },
    )

    // Add items from visit tasks (Markku employee work)
    const visits = (visitsData?.data || []).flatMap(
      (visit: VisitWithRelations) => {
        // Only include completed visits or visits in the past
        const visitDate = new Date(visit.startTime)
        const isPast = visitDate < new Date()
        if (!isPast && visit.status !== 'completed') return []

        const visitTasks = visit.visitTasks || []
        const visitAttachments = visit.attachments || []

        return visitTasks.map((task) => {
          // Use task completion date or visit completion date
          const completedDate = new Date(
            task.completedAt || visit.completedAt || visit.startTime,
          )

          // Filter attachments for this task (by stepId in metadata)
          const taskStepIds = (task.steps || []).map((s) => s.id)
          const taskAttachments = visitAttachments.filter(
            (att: AttachmentResponse) =>
              taskStepIds.includes(att.metadata?.stepId),
          )

          return {
            id: `visit-${visit.id}-task-${task.id}`,
            completedDate,
            taskName: task.taskName,
            description: task.taskDescription,
            category:
              task.maintenanceTask?.category || task.taskCategory || null,
            notes: task.notes,
            isMarkku: true,
            visitId: visit.id,
            attachments: taskAttachments,
          } satisfies UnifiedMaintenanceItem
        })
      },
    )

    // Sort by completion date descending
    const allItems = [...logs, ...visits].filter(isTruthy)
    return sortBy(allItems, [(item) => item.completedDate, 'desc'])
  }, [logsData, visitsData])

  // Extract unique years and categories
  const { availableYears, availableCategories } = useMemo(() => {
    const years = new Set<string>()
    const categories = new Set<string>()

    unifiedItems.forEach((item) => {
      years.add(item.completedDate.getFullYear().toString())
      if (item.category) {
        categories.add(item.category)
      }
    })

    // Add current year
    years.add(new Date().getFullYear().toString())

    return {
      availableYears: Array.from(years).sort((a, b) => Number(b) - Number(a)),
      availableCategories: Array.from(categories).sort(),
    }
  }, [unifiedItems])

  // Filter items
  const filteredItems = useMemo(() => {
    return unifiedItems.filter((item) => {
      // Filter by year
      if (selectedYear) {
        const itemYear = item.completedDate.getFullYear().toString()
        if (itemYear !== selectedYear) return false
      }

      // Filter by category
      if (selectedCategory && item.category !== selectedCategory) {
        return false
      }

      return true
    })
  }, [unifiedItems, selectedYear, selectedCategory])

  const handleCardClick = (item: UnifiedMaintenanceItem) => {
    // If it's a Markku task with a visitId, navigate to the visit page
    if (item.isMarkku && item.visitId) {
      navigate({ to: `/visits/${item.visitId}` })
    } else if (!item.isMarkku) {
      // For user's own logs, navigate to detail page
      // Extract numeric ID from "log-123" format
      const logId = item.id.replace('log-', '')
      navigate({ to: `/maintenance-log/${logId}` })
    }
  }

  const renderItems = () => {
    if (filteredItems.length === 0) {
      return (
        <Card withBorder radius="md" p="xl">
          <Center>
            <Stack align="center" gap="xs">
              <Text c="dimmed" size="sm">
                <Trans>
                  No completed maintenance tasks with selected filters
                </Trans>
              </Text>
            </Stack>
          </Center>
        </Card>
      )
    }

    let lastYear: string | null = null
    const elements: React.ReactNode[] = []

    filteredItems.forEach((item) => {
      const itemYear = item.completedDate.getFullYear().toString()

      // Add year separator if year changed
      if (itemYear !== lastYear) {
        elements.push(
          <Divider
            key={`year-${itemYear}`}
            label={
              <Text fw={600} size="sm" c="dimmed">
                {t`${itemYear} maintenance activities`}
              </Text>
            }
            labelPosition="center"
            mt={lastYear ? 'xl' : 0}
            mb="md"
          />,
        )
        lastYear = itemYear
      }

      const day = item.completedDate.getDate()
      const month = item.completedDate.toLocaleDateString('fi-FI', {
        month: 'short',
      })

      elements.push(
        <Card
          key={item.id}
          withBorder
          radius="md"
          p="sm"
          style={{ cursor: 'pointer' }}
          onClick={() => handleCardClick(item)}
        >
          <Stack gap="sm">
            <Group align="flex-start" wrap="nowrap">
              {/* Date section */}
              <Box
                style={{
                  textAlign: 'center',
                  minWidth: 50,
                  paddingRight: 'var(--mantine-spacing-xs)',
                  borderRight: '1px solid var(--mantine-color-gray-2)',
                  marginRight: 'var(--mantine-spacing-sm)',
                }}
              >
                <Text size="xl" fw={700} lh={1}>
                  {day}
                </Text>
                <Text size="xs" c="dimmed" tt="uppercase">
                  {month}
                </Text>
              </Box>

              {/* Main content */}
              <Stack gap="xs" style={{ flex: 1 }}>
                <Group justify="space-between" align="flex-start">
                  <Box>
                    <Text fw={500} size="md" mb={4}>
                      {item.taskName}
                    </Text>
                    <Group gap="xs">
                      {item.category && (
                        <Badge
                          size="sm"
                          variant="light"
                          color={getCategoryColor(item.category)}
                        >
                          {getCategoryLabel(item.category)}
                        </Badge>
                      )}
                      {item.isMarkku && (
                        <Badge
                          size="sm"
                          color="brandOrange"
                          variant="light"
                          leftSection={<IconUser size={12} />}
                        >
                          Markku
                        </Badge>
                      )}
                    </Group>
                  </Box>

                  {/* Photo count indicator */}
                  {item.attachments.length > 0 && (
                    <Group gap={4}>
                      <IconPhoto
                        size={16}
                        color="var(--mantine-color-gray-6)"
                      />
                      <Text size="xs" c="dimmed">
                        {item.attachments.length}
                      </Text>
                    </Group>
                  )}
                </Group>

                {item.description && (
                  <Text size="sm" c="dimmed" lineClamp={2}>
                    {item.description}
                  </Text>
                )}

                {item.notes && (
                  <Text size="xs" c="dimmed" fs="italic">
                    "{item.notes}"
                  </Text>
                )}
              </Stack>
            </Group>
          </Stack>
        </Card>,
      )
    })

    return elements
  }

  // Add button in title bar
  const addButton = propertyId ? (
    <Button
      leftSection={<IconPlus size={16} />}
      onClick={openAddModal}
      variant="filled"
      color="brandOrange"
      size="sm"
    >
      <Trans>Add</Trans>
    </Button>
  ) : undefined

  return (
    <>
      <AppPage
        title={t`Maintenance history`}
        subtitle={t`Completed maintenance tasks`}
        backTarget="/maintenance"
        rightSection={addButton}
      >
        <Stack gap="md">
          {/* Filters */}
          <Card withBorder radius="md" p="md">
            <Group grow>
              <Select
                label={t`Year`}
                placeholder={t`Select year`}
                value={selectedYear}
                onChange={setSelectedYear}
                data={availableYears.map((year) => ({
                  value: year,
                  label: year,
                }))}
                clearable
              />
              <Select
                label={t`Category`}
                placeholder={t`All categories`}
                value={selectedCategory}
                onChange={setSelectedCategory}
                data={availableCategories.map((cat) => ({
                  value: cat,
                  label: getCategoryLabel(cat),
                }))}
                clearable
              />
            </Group>
          </Card>

          {/* Loading state */}
          {isLoading && (
            <Center py="xl">
              <Loader size="md" />
            </Center>
          )}

          {/* Items list */}
          {!isLoading && renderItems()}
        </Stack>
      </AppPage>

      {/* Add Log Modal */}
      {propertyId && (
        <TaskDetailsModal
          propertyId={propertyId}
          opened={addModalOpened}
          onClose={closeAddModal}
          defaultCompleted={true}
        />
      )}
    </>
  )
}

export const Route = createFileRoute('/_app/maintenance-log/')({
  component: MaintenanceLogPage,
})
